// ══════════════════════════════════════════════════════
//  fetchInstrument.js — 统一抓取层日志 + dataSourceHealth 累积
//
//  目的：每个外部数据源 fetch 都打一行 [source-fetch] 日志（用户报 bug
//  时不用反复猜），同时把每次结果累积进 result._debug.dataSourceHealth，
//  让详情页 footer 的 "🔍 数据源诊断" 按钮一目了然。
//
//  用法：
//    const dsh = createHealthCollector();
//    const cgData = await dsh.wrap('coingecko', { project: 'Sui' }, () => cgGet(...), r => ({
//      ok: !!r, ms: undefined, sample: { tickers: r?.tickers?.length, fdv: r?.market_data?.fully_diluted_valuation?.usd }
//    }));
//    // ...
//    return { ..., _debug: { dataSourceHealth: dsh.snapshot() } };
// ══════════════════════════════════════════════════════

function summarize(result, summarizer) {
  if (typeof summarizer === 'function') {
    try { return summarizer(result); } catch { return null; }
  }
  return null;
}

/**
 * 创建一个 health collector（每个 fetchFullProject 调用一个实例）。
 * 不复用全局单例，避免 React 多项目分析并发互相污染。
 */
export function createHealthCollector() {
  const entries = {}; // { source: { ok, ms, sample, error, attempts, ts } }

  /**
   * Wrap a fetch promise — log + accumulate.
   *
   * @param {string} source — 'coingecko' | 'defillama' | 'rootdata' | 'surf-news' | 'surf-unlock' | ...
   * @param {object} ctx — { project, ...extras } 项目名 + 任意上下文（slug / symbol / endpoint）
   * @param {Function} fn — async () => result
   * @param {Function} [summarizer] — (result) => { ok?: bool, sample?: object } 提取关键字段
   * @returns {Promise<result>} 透传原 result（含 null / throws）
   */
  async function wrap(source, ctx, fn, summarizer) {
    const t0 = Date.now();
    const project = ctx?.project || '?';
    const ctxStr = Object.entries(ctx || {})
      .filter(([k]) => k !== 'project')
      .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join(' ');
    let result, error = null;
    try {
      result = await fn();
    } catch (e) {
      error = e;
    }
    const ms = Date.now() - t0;
    const summary = summarize(result, summarizer);
    const ok = error ? false : (summary?.ok != null ? !!summary.ok : (result != null && result !== false));
    const entry = {
      ok,
      ms,
      sample: summary?.sample ?? null,
      error: error ? (error.message || String(error)).slice(0, 200) : null,
      ts: new Date().toISOString(),
    };
    // 多次调用同源则合并，记录最近一次
    entries[source] = entry;

    const sampleStr = entry.sample ? ' ' + Object.entries(entry.sample).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' ') : '';
    const msg = `[${source}-fetch] "${project}"${ctxStr ? ' ' + ctxStr : ''} ok=${ok}${error ? ` err="${entry.error}"` : ''}${sampleStr} ms=${ms}`;
    if (ok) console.log(msg);
    else console.warn(msg);

    if (error && !result) throw error; // 调用方期待透明传递
    return result;
  }

  /** Return the accumulated map */
  function snapshot() {
    return { ...entries };
  }

  return { wrap, snapshot };
}
